// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { generatePageTitle } from "@gcg/App/Utilities/setPageMetaData";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    products: getProductListDataView(state).value,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductListControlsContainerStyles {
    container?: GridContainerProps;
    countGridItem?: GridItemProps;
    sortGridItem?: GridItemProps;
    viewSelectGridItem?: GridItemProps;
}

export const controlsContainerStyles: ProductListControlsContainerStyles = {
    container: {
        gap: 0,
    },
    countGridItem: {
        width: 12,
    },
    sortGridItem: {
        css: css`
            width: 215px;
            max-width: 215px;
            flex-basis: 215px;
            padding-right: 10px;
            padding-top: 14px;
            padding-bottom: 14px;
            border-bottom: 1px solid ${getColor("common.ui2")};
            &&.remove-padding {
                padding-top: 0;
                padding-bottom: 8px;
            }
            @media only screen and (max-width: 1140px) {
                width: 160px;
                max-width: 160px;
                flex-basis: 160px;
                label {
                    display: none;
                }
            }

            @media only screen and (max-width: 600px) {
                width: 100%;
                max-width: 100%;
                flex-basis: 100%;
                border-bottom: 0;
                padding-right: 0;
            }
        `,
    },
    viewSelectGridItem: {
        css: css`
            width: calc(100% - 215px);
            max-width: calc(100% - 215px);
            flex-basis: calc(100% - 215px);
            padding-top: 14px;
            padding-bottom: 14px;
            border-bottom: 1px solid ${getColor("common.ui2")};
            &&.remove-padding {
                padding-top: 0;
                padding-bottom: 8px;
            }
            @media only screen and (max-width: 1140px) {
                width: calc(100% - 160px);
                max-width: calc(100% - 160px);
                flex-basis: calc(100% - 160px);
                label {
                    display: none;
                }
            }

            > div {
                display: flex;
                justify-content: flex-end;
                align-items: center;
            }

            @media only screen and (max-width: 1140px) {
                label {
                    display: none;
                }
            }

            @media only screen and (max-width: 600px) {
                width: 100%;
                max-width: 100%;
                flex-basis: 100%;

                > div {
                    justify-content: center;
                }
            }
        `,
    },
};

const styles = controlsContainerStyles;

const ProductListControlsContainer: FC<Props> = ({ id, products }) => {
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.countGridItem}>
                <Zone contentId={id} zoneName="Content00" />
            </GridItem>
            <GridItem {...styles.sortGridItem} className={!products?.length ? "remove-padding" : ""}>
                <Zone contentId={id} zoneName="Content01" />
            </GridItem>
            <GridItem {...styles.viewSelectGridItem} className={!products?.length ? "remove-padding" : ""}>
                <Zone contentId={id} zoneName="Content02" />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListControlsContainer),
    definition: {
        group: "Product List",
        displayName: "List Controls Container",
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
